import './events.scss';

import {
  Button,
  Card,
  Colors,
  H5,
  H6,
  Intent,
  Tab,
  Tabs,
  Tag,
  Popover,
  Classes,
} from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { formatDate, parseDate } from 'utils/format';
import produce from 'utils/produce';
import useModalContext from 'components/context/modal';

import useEditorItemContext from '../../context/editorItemContext';
import useEditorContext from '../../context/editorContext';

import DialogPanelBody from '../components/dialogPanelBody';
import OPTIONS from '.';
import generateEventsData from '../../generator/generateEventsData';
import uniqBy from 'utils/uniqBy';

const Root = styled.div`
  display: flex;
  min-height: 580px;
  max-height: 680px;
`;

const LeftSide = styled.div`
  flex: 1;
  padding-right: 20px;
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  max-width: 500px;
`;

const TopActionButton = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: end;
`;

const RightSideCard = styled(Card)`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const EventList = styled.div`
  flex: 1;
  overflow: auto;
`;

const StyledTabs = styled(Tabs)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const EventDate = styled.div`
  display: flex;
  margin-bottom: 5px;
  align-items: center;
`;

const Date = styled.strong`
  width: 30px;
`;

const Tags = styled.div`
  flex: 1;
`;

const PopoverContent = styled.div`
  padding: 20px;
`;

const StyledTag = styled(Tag)`
  margin: 1px;
  background-color: ${props => {
    switch (props.$eventType) {
      case 'ancestry':
        return 'rgba(107,164,16,.15)';
      case 'familysearch':
        return 'rgba(222, 121, 20, 0.15)';
      case 'holidays':
        return 'rgba(217,130,43,.15)';
      case 'creations':
        return 'rgba(19,124,189,.15)';
      case 'classic':
        return 'rgba(43,185,217,.15)';
      default:
        return 'rgba(219,55,55,.15)';
    }
  }} !important;
  color: ${props => {
    switch (props.$eventType) {
      case 'ancestry':
        return '#6ba410';
      case 'familysearch':
        return '#f69054';
      case 'holidays':
        return '#bf7326';
      case 'creations':
        return '#106ba3';
      case 'classic':
        return '#10839d';
      default:
        return '#c23030';
    }
  }} !important;
`;

const ContentWizardPanel = ({ params, closeDialog, openPanel }) => {
  const { item, template, config } = useEditorItemContext();
  const editorContext = useEditorContext();
  const modalContext = useModalContext();

  const [isLoading, setIsLoading] = useState(false);

  const panels = useMemo(() => {
    const value = [];
    if (config?.contentOptions?.events) {
      config.contentOptions.events.forEach(key => {
        if (OPTIONS[key]) {
          value.push({
            key,
            ...OPTIONS[key],
          });
        }
      });
    }
    if (item?.additionalInfo?.source) {
      const key = item.additionalInfo.source
      if (OPTIONS[key]) {
        value.unshift({
          key,
          ...OPTIONS[key],
        });
      }
    }
    return uniqBy(value, "key");
  }, [config.contentOptions, item.additionalInfo]);

  const events = useMemo(() => {
    const initialMonth = parseDate(item.additionalInfo.initialMonth, 'YYYY-MM');

    const getRealMonth = eventDate => {
      let year = initialMonth.getFullYear();
      if (eventDate.getMonth() < initialMonth.getMonth()) {
        year += 1;
      }
      return `${year}-${formatDate(eventDate, 'MM')}`;
    };

    const data = {};
    if (item.additionalInfo?.events) {
      item.additionalInfo.events.forEach(event => {
        const date = parseDate(event.date, 'YYYY-MM-DD');

        const month = getRealMonth(date);
        const day = formatDate(date, 'DD');
        if (!data[month]) {
          data[month] = {};
        }
        if (!data[month][day]) {
          data[month][day] = [];
        }
        data[month][day].push(event);
      });
    }

    return Object.entries(data)
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(i => {
        try {
          const month = formatDate(parseDate(i[0], 'YYYY-MM'), 'MMMM YYYY');
          const days = Object.entries(i[1])
            .sort((a, b) => (a[0] < b[0] ? -1 : 1))
            .map(j => {
              try {
                const day = formatDate(parseDate(j[0], 'DD'), 'D');
                return [day, j[1]];
              } catch (e) {
                console.error(`Date Format Error - ${j[0]}`, e);
                return null;
              }
            })
            .filter(j => !!j);
          return [month, days];
        } catch (e) {
          console.error(`Date Format Error - ${i[0]}`, e);
          return null;
        }
      })
      .filter(i => !!i);
  }, [item.additionalInfo]);

  const numberOfEvents = useMemo(
    () => item.additionalInfo?.events?.length ?? 0,
    [item.additionalInfo?.events],
  );

  const handleRemoveAllEvents = () => {
    editorContext.filterEvents(item.id);
  };

  const handleRemoveEvent = key => {
    editorContext.filterEvents(item.id, event => event.key !== key);
  };

  const handleUpdateData = async () => {
    setIsLoading(true);
    const { data, error } = await generateEventsData(
      editorContext.accessToken,
      template,
      item.additionalInfo.events,
      item.additionalInfo.initialMonth,
      item.additionalInfo.initialDay,
      item.additionalInfo.source,
    );

    const showErrorModal = content => {
      setIsLoading(false);
      modalContext.confirm(content, {
        title: 'Error creating your project',
        closeable: false,
        confirmLabel: 'OK',
      });
    };

    if (error) {
      showErrorModal(<>{error}</>);
      return;
    }

    if (item.pages.length !== data.pages.length) {
      showErrorModal(<>Oops, different number of pages!</>);
      return;
    }

    // merge pages
    const newPages = item.pages.map((oldPage, index) => {
      const newPage = data.pages[index];

      return produce(oldPage, draft => {
        // remove imported-events-* placeholders which are not defined in additionalInfo
        const oldPlaceholders = oldPage.placeholders.filter(({ id }) => {
          if (id.indexOf('imported-event') === 0) {
            const key = id.replace('imported-event-', '');
            // keep event which is in the event list
            return item.additionalInfo.events.findIndex(e => e.key === key) >= 0;
          }
          return true;
        });

        // add imported-events-* placeholders which are not in previous list of placeholders
        const newPlaceholders = newPage.placeholders.filter(({ id }) => {
          if (id.indexOf('imported-event') === 0) {
            if (oldPlaceholders.findIndex(p => p.id === id) < 0) {
              return true;
            }
          }
          return false;
        });

        draft.placeholders = [...oldPlaceholders, ...newPlaceholders];
      });
    });

    editorContext.setPages(item.id, newPages);
    editorContext.addPhotos(item.id, data.photos);
    closeDialog();
  };

  return (
    <DialogPanelBody>
      <Root>
        <LeftSide>
          {panels.length > 0 && (
            <StyledTabs defaultSelectedTabId={panels[0].key}>
              {panels.map(panel => (
                <Tab
                  key={panel.key}
                  id={panel.key}
                  panelClassName="event-panel"
                  title={panel.title}
                  panel={<panel.Component />}
                />
              ))}
            </StyledTabs>
          )}
        </LeftSide>
        <RightSide>
          <TopActionButton>
            <Button loading={isLoading} onClick={handleUpdateData} intent={Intent.PRIMARY}>
              Update My Calendar
            </Button>
          </TopActionButton>
          <RightSideCard>
            <H5 className="text-center">Events Displayed on this Calendar</H5>
            <p className="text-center mb-1">
              It is recommended to use a maximum of 3 events per day.
            </p>

            <EventList>
              {events.map(([month, days]) => (
                <div key={month} className="mb-1">
                  <H6>{month}</H6>
                  {days.map(([date, items]) => (
                    <EventDate key={date}>
                      <Date>{date}</Date>
                      <Tags>
                        {items.map(event => (
                          <StyledTag
                            key={event.key}
                            onRemove={() => handleRemoveEvent(event.key)}
                            minimal
                            $eventType={event.type}
                          >
                            {event.description}
                          </StyledTag>
                        ))}
                      </Tags>
                    </EventDate>
                  ))}
                </div>
              ))}
            </EventList>

            {numberOfEvents > 0 && (
              <Popover
                placement="top"
                content={
                  <PopoverContent>
                    <H5 style={{ color: Colors.BLACK }}>Confirm deletion</H5>
                    <p className="mb-1">Do you really want to delete all events?</p>
                    <div className="d-flex justify-content-end">
                      <Button className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }}>
                        Cancel
                      </Button>
                      <Button
                        className={Classes.POPOVER_DISMISS}
                        onClick={handleRemoveAllEvents}
                        intent={Intent.DANGER}
                      >
                        Confirm
                      </Button>
                    </div>
                  </PopoverContent>
                }
              >
                <Button fill intent={Intent.DANGER} className="mt-1">
                  Remove All Events ({numberOfEvents})
                </Button>
              </Popover>
            )}
          </RightSideCard>
        </RightSide>
      </Root>
    </DialogPanelBody>
  );
};

ContentWizardPanel.propTypes = {
  closeDialog: PropTypes.func.isRequired,
  openPanel: PropTypes.func.isRequired,
};

export default ContentWizardPanel;
